import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import axiosInstance from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../config';
import { Link as RouterLink } from 'react-router-dom';
import { FormCard, FormContainer } from '../SharedFormStyles';

// Styled Components
const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  backgroundImage:
    'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
  backgroundRepeat: 'no-repeat',
}));

export default function SignUp() {
  const [mode, setMode] = useState('light');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    number: false,
    uppercase: false,
    lowercase: false,
    specialChar: false,
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordMatch, setPasswordMatch] = useState(true);


  useEffect(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      setMode(savedMode);
    } else {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(systemPrefersDark ? 'dark' : 'light');
    }
  }, []);

  const validatePassword = (password) => {
    setPasswordValidation({
      length: password.length >= 8,
      number: /\d/.test(password),
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    validatePassword(value);
    setFormData({ ...formData, password: value });
    setPasswordMatch(value === formData.confirmPassword);
  };
  
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, confirmPassword: value });
    setPasswordMatch(value === formData.password);
  };  
  
  const validateInputs = (data) => {
    const newErrors = {};
  
    if (!data.name || data.name.length < 1) {
      newErrors.name = 'Bitte geben Sie Ihren Namen ein.';
    }
    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    }
    const passwordError = validatePassword(data.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }
    if (data.password !== data.confirmPassword) {
      newErrors.confirmPassword = 'Die Passwörter stimmen nicht überein.';
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
  
    if (!validateInputs(Object.fromEntries(data))) return;
  
    setLoading(true);
    setErrorMessage('');
    setErrors({});
  

    try {
      await axiosInstance.post('/auth/register', {
        name: data.get('name'),
        email: data.get('email'),
        password: data.get('password'),
      });

      navigate('/pages/confirmation');
    } catch (error) {
      console.error('Fehler beim Registrieren:', error);
    
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setErrors({
              email: 'Diese E-Mail-Adresse existiert bereits.',
              password: '',
              name: '',
            });
            break;
            case 422:
              setErrors({
                email: '',
                password: 'Ihr Passwort erfüllt nicht die erforderlichen Kriterien.',
                name: '',
              });
              break;
          case 500:
            setErrors({
              email: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
              password: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
              name: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
            });
            setErrorMessage(
              'Ein Fehler ist beim Senden der Bestätigungsmail aufgetreten. Bitte wenden Sie sich an unseren Support: info@schullogbuch24.de'
            );
            break;
          default:
            setErrorMessage('Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        }
      } else {
        setErrorMessage('Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={createTheme({ palette: { mode } })}>
      <CssBaseline enableColorScheme />
      <FormContainer direction="column" justifyContent="space-between" sx={{backgroundImage: 'none'}}>
        <FormCard variant="outlined">
          <Typography component="h1" variant="h4">Registrieren</Typography>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl>
            <FormLabel>Vollständiger Name</FormLabel>
            <TextField
              name="name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
          </FormControl>
          <FormControl>
            <FormLabel>E-Mail</FormLabel>
            <TextField
              name="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Passwort</FormLabel>
            <TextField
              name="password"
              type="password"
              value={formData.password || ''}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                handlePasswordChange(e);
              }}
              error={!!errors.password}
              helperText={errors.password}
            />
          </FormControl>
          <Box>
            <Typography variant="body2">
              {passwordValidation.length ? (
                <CheckCircleIcon sx={{ color: 'green', fontSize: 16 }} />
              ) : (
                <CancelIcon sx={{ color: 'red', fontSize: 16 }} />
              )}{' '}
              Mindestens 8 Zeichen
            </Typography>
            <Typography variant="body2">
              {passwordValidation.number ? (
                <CheckCircleIcon sx={{ color: 'green', fontSize: 16 }} />
              ) : (
                <CancelIcon sx={{ color: 'red', fontSize: 16 }} />
              )}{' '}
              Mindestens eine Zahl
            </Typography>
            <Typography variant="body2">
              {passwordValidation.uppercase ? (
                <CheckCircleIcon sx={{ color: 'green', fontSize: 16 }} />
              ) : (
                <CancelIcon sx={{ color: 'red', fontSize: 16 }} />
              )}{' '}
              Mindestens ein Großbuchstabe
            </Typography>
            <Typography variant="body2">
              {passwordValidation.lowercase ? (
                <CheckCircleIcon sx={{ color: 'green', fontSize: 16 }} />
              ) : (
                <CancelIcon sx={{ color: 'red', fontSize: 16 }} />
              )}{' '}
              Mindestens ein Kleinbuchstabe
            </Typography>
            <Typography variant="body2">
              {passwordValidation.specialChar ? (
                <CheckCircleIcon sx={{ color: 'green', fontSize: 16 }} />
              ) : (
                <CancelIcon sx={{ color: 'red', fontSize: 16 }} />
              )}{' '}
              Mindestens ein Sonderzeichen
            </Typography>
          </Box>
          <FormControl>
            <FormLabel>Passwort bestätigen</FormLabel>
            <TextField
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword || ''}
              onChange={handleConfirmPasswordChange}
              error={!!errors.confirmPassword || !passwordMatch}
              helperText={
                errors.confirmPassword ? (
                  errors.confirmPassword
                ) : !passwordMatch ? (
                  <Typography variant="body2" sx={{ color: 'red' }}>
                    Die Passwörter stimmen nicht überein.
                  </Typography>
                ) : formData.confirmPassword ? (
                  <Typography variant="body2" sx={{ color: 'green' }}>
                    Die Passwörter stimmen überein.
                  </Typography>
                ) : null
              }
            />
          </FormControl>
          <Button type="submit" fullWidth variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Registrieren'}
          </Button>
          <Typography sx={{ textAlign: 'center' }}>
            Sie haben bereits einen Account?{' '}
            <Link component={RouterLink} to="/pages/login">Anmelden</Link>
          </Typography>
          </Box>
        </FormCard>
      </FormContainer>
    </ThemeProvider>
  );
}